import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import CannonDebugger from 'cannon-es-debugger';

import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';


import { WheelInfo } from 'cannon';
import * as Utils from './Utils.js';

class Seat {
    seatedObject;
    constructor(object, gltf) {
        this.seatPointObject = object;

    }
}
class Wheel {
    wheelObjec;
    position;
    steering;
    drive;
    rayCastWheelInfoIndex;

    constructor(wheelObject) {
        this.wheelObject = wheelObject;

        this.position = wheelObject.position;

        if (wheelObject.hasOwnProperty('userData') && wheelObject.userData.hasOwnProperty('data')) {
            if (wheelObject.userData.hasOwnProperty('steering')) {
                this.steering = (wheelObject.userData.steering === 'true');
            }

            if (wheelObject.userData.hasOwnProperty('drive')) {
                this.drive = wheelObject.userData.drive;
            }
        }
    }
}

class SpringSimulation {
    /**
     * @param {number} mass - How heavy the spring feels. Higher values are slower.
     * @param {number} stiffness - How strong the spring is. Higher values are snappier.
     * @param {number} damping - How much the spring resists oscillation. A value of ~0.5 is critically damped (no bounce).
     */
    constructor(mass, stiffness, damping) {
        this.position = 0;
        this.velocity = 0;
        this.target = 0;

        // These are calculated from the more intuitive parameters above
        this.w0 = Math.sqrt(stiffness / mass); // Natural frequency
        this.zeta = damping / (2 * Math.sqrt(stiffness * mass)); // Damping ratio
    }

    /**
     * Updates the simulation state for a given time step.
     * @param {number} timeStep - The time elapsed since the last update.
     */
    simulate(timeStep) {
        // Clamp timeStep to prevent physics explosions
        if (timeStep > 0.1) {
            timeStep = 0.1;
        }

        const w0_dt = this.w0 * timeStep;

        // Exact solution for a critically damped spring (zeta = 1)
        if (this.zeta >= 1.0) {
            const a = this.position - this.target;
            const b = this.velocity + a * this.w0;
            const exp_w0_dt = Math.exp(-w0_dt);

            this.position = this.target + (a + b * timeStep) * exp_w0_dt;
            this.velocity = (-a * this.w0 - b + b * w0_dt) * exp_w0_dt;
            return;
        }

        // Exact solution for an underdamped spring (zeta < 1)
        const wd = this.w0 * Math.sqrt(1.0 - this.zeta * this.zeta);
        const wd_dt = wd * timeStep;
        const exp_zeta_w0_dt = Math.exp(-this.zeta * w0_dt);
        const cos_wd_dt = Math.cos(wd_dt);
        const sin_wd_dt = Math.sin(wd_dt);

        const a = this.position - this.target;
        const b = (this.velocity + a * this.zeta * this.w0) / wd;

        this.position = this.target + exp_zeta_w0_dt * (a * cos_wd_dt + b * sin_wd_dt);
        this.velocity = -exp_zeta_w0_dt * ((a * this.zeta * this.w0 - b * wd) * cos_wd_dt + (a * wd + b * this.zeta * this.w0) * sin_wd_dt);
    }
}
class CarTest {
    /**
     * @param {THREE.Scene} scene - The main three.js scene.
     * @param {CANNON.World} world - The main cannon.js physics world.
     * @param {THREE.GLTFLoader} gltfLoader - An instance of the GLTFLoader.
     * 
     
     */
    scene;
    world;
    camera;
    gameContainer;
    ui2d;
    gltfLoader;
    renderer;
    model;
    chassisBody;
    chassisMesh;
    vehicleVisual;
    seats = [];
    wheels = [];
    steeringWheel;
    controls;
    textureLoader = new THREE.TextureLoader();


    startPos;
    constructor(scene, world, position, groundMaterial) {

        this.scene = scene;
        this.world = world;
        this.gltfLoader = new GLTFLoader();
        this.startPos = position;
        this.model = null;
        this.chassisBody = null;
        this.raycastVehicle = null;
        this.steeringWheel = null; // Reference to the steering wheel mesh

        // These parameters can be tweaked to change the car's handling
        this.vehicleParams = {
            chassisMass: 150,
            wheelMaterial: new CANNON.Material('wheel',),
            suspensionStiffness: 30,
            suspensionRestLength: 0.15,
            suspensionDamping: 3,
            suspensionCompression: 4.4,
            maxSuspensionTravel: 2,
            dampingRelaxation: 2,
            dampingCompression: 2,
            rollInfluence: 0.2, // How much the chassis rolls
            frictionSlip: 1.2, // Higher friction means better grip
            engineForce: 120,
            maxSteerVal: 0.5, // Maximum steering angle

        };

        this.steeringSimulator = new SpringSimulation(0.1, 2, 0.5);

        this.lastForwardSpeed = 0;
        this.deceleration = 0;
        this.steeringValue = 0;
    }



    /**
     * Initializes the car controller by creating the road,
     * loading the model, and setting up input listeners.
     */
    async _init() {
        await this._loadCarModel();
    }
    getWheelSlip() {
        // This function is now ONLY responsible for calculating sideways/lateral slip.
        // The slip from hard braking is now handled in the main Carmagedon game loop.
        if (!this.chassisBody) return 0;

        const body = this.chassisBody;
        const velocity = body.velocity;

        // Get car's local right-direction vector in world space
        const rightVectorWorld = new CANNON.Vec3(1, 0, 0);
        body.quaternion.vmult(rightVectorWorld, rightVectorWorld);

        const forwardSpeed = this.getForwardSpeed();
        const lateralSpeed = Math.abs(velocity.dot(rightVectorWorld));

        // Calculate slip based on the ratio of lateral to forward speed
        if (Math.abs(forwardSpeed) < 1.0) {
            // At very low speeds, any lateral movement is high slip
            return THREE.MathUtils.clamp(lateralSpeed / 2, 0, 1);
        } else {
            // At higher speeds, it's about the ratio
            const slipRatio = lateralSpeed / Math.abs(forwardSpeed);
            // The multiplier makes the effect more pronounced
            return THREE.MathUtils.clamp(slipRatio * 2.5, 0, 1);
        }
    }
    _loadCarModel() {
        return new Promise((resolve) => {
            this.gltfLoader.load('assets/carmagedon/car.glb', (gltf) => {
                this.model = gltf.scene;


                const carChassisMaterial = new CANNON.Material('carChassis');

                // Create the chassis physics body
                this.chassisBody = new CANNON.Body({ mass: this.vehicleParams.chassisMass, material: carChassisMaterial });
                this.chassisBody.position.copy(this.startPos || new THREE.Vector3(0, 0, 0));
                this.chassisBody.linearDamping = 0.1;  // default 0.01
                this.chassisBody.angularDamping = 0.; // helps with spin

                this.chassisMesh = this.model.getObjectByName('body');
                const initialChassisPosition = new THREE.Vector3();
                this.chassisMesh.getWorldPosition(initialChassisPosition);



                gltf.scene.traverse((child) => {
                    child.castShadow = true;
                    if (child.userData.data === 'collision') {
                        if (child.userData.shape === 'box') {
                            child.visible = false;
                            let phys = new CANNON.Box(new CANNON.Vec3(child.scale.x, child.scale.y, child.scale.z));
                            phys.collisionFilterMask = ~4;
                            this.chassisBody.addShape(phys, new CANNON.Vec3(child.position.x, child.position.y, child.position.z));

                        }
                        else if (child.userData.shape === 'sphere') {
                            child.visible = false;
                            let phys = new CANNON.Sphere(child.scale.x);
                            phys.collisionFilterGroup = 4;
                            this.chassisBody.addShape(phys, new CANNON.Vec3(child.position.x, child.position.y, child.position.z));
                        }
                    }
                    if (child.userData.data === 'seat') {
                        this.seats.push(new Seat(child, gltf));
                    }
                    if (child.userData.data === 'wheel') {
                        this.wheels.push(new Wheel(child));
                    }
                    if (child.hasOwnProperty('userData')) {
                        if (child.userData.hasOwnProperty('data')) {
                            if (child.userData.data === 'steering_wheel') {
                                this.steeringWheel = child;
                            }
                        }
                    }

                })

                if (this.chassisMesh && this.seats.length > 0) {
                    this.seats.forEach(seat => {
                        console.log(`Attaching seat '${seat.seatPointObject.name}' to chassis '${this.chassisMesh.name}'`);
                        this.chassisMesh.attach(seat.seatPointObject); // The magic happens here!
                    });
                }
                // Set up the CANNON.RaycastVehicle
                this.raycastVehicle = new CANNON.RaycastVehicle({
                    chassisBody: this.chassisBody,
                    indexRightAxis: 0, // x
                    indexForwardAxis: 2, // z
                    indexUpAxis: 1, // y
                });

                const handlingSetup = {
                    radius: 0.25,
                    suspensionStiffness: this.vehicleParams.suspensionStiffness,
                    suspensionRestLength: this.vehicleParams.suspensionRestLength,
                    maxSuspensionTravel: this.vehicleParams.maxSuspensionTravel,
                    frictionSlip: this.vehicleParams.frictionSlip,
                    dampingRelaxation: this.vehicleParams.dampingRelaxation,
                    dampingCompression: this.vehicleParams.dampingCompression,
                    rollInfluence: this.vehicleParams.rollInfluence,
                };
                handlingSetup.chassisConnectionPointLocal = new CANNON.Vec3();
                handlingSetup.axleLocal = new CANNON.Vec3(-1, 0, 0);
                handlingSetup.directionLocal = new CANNON.Vec3(0, -1, 0);

                this.wheels.forEach((wheel) => {
                    handlingSetup.chassisConnectionPointLocal.set(wheel.position.x, wheel.position.y, wheel.position.z - 0.1);
                    const index = this.raycastVehicle.addWheel(handlingSetup);
                    wheel.rayCastWheelInfoIndex = index;
                });

                this.raycastVehicle.addToWorld(this.world);

                // Assign the wheel material to the physics wheels
                this.raycastVehicle.wheelInfos.forEach(wheel => {
                    const cylinderShape = new CANNON.Cylinder(wheel.radius, wheel.radius, 0.2, 20);
                    const body = new CANNON.Body({ mass: 1, material: this.vehicleParams.wheelMaterial });
                    const q = new CANNON.Quaternion().setFromAxisAngle(new CANNON.Vec3(0, 0, 1), Math.PI / 2);
                    body.addShape(cylinderShape, new CANNON.Vec3(), q);
                });


                this.scene.add(this.model);
                resolve();
            });
        });
    }

    setSteeringValue(val) {
        this.wheels.forEach((wheel) => {
            if (wheel.steering) this.raycastVehicle.setSteeringValue(val, wheel.rayCastWheelInfoIndex);
        });
    }

    applyEngineForce(force) {
        this.wheels.forEach((wheel) => {
            if (wheel.drive) {
                this.raycastVehicle.applyEngineForce(force, wheel.rayCastWheelInfoIndex);
            }
        });
    }

    canTiltForwards = false;
    airSpinTimer = 0;
    speed = 5;
    airSpin(timeStep, throttle, steering) {
        const quat = Utils.threeQuat(this.chassisBody.quaternion);
        const forward = new THREE.Vector3(0, 0, 1).applyQuaternion(quat);
        const right = new THREE.Vector3(1, 0, 0).applyQuaternion(quat);
        const up = new THREE.Vector3(0, 1, 0).applyQuaternion(quat);
        //air spin
        let airSpinInfluence = THREE.MathUtils.clamp(this.airSpinTimer / 4, 0, 1);
        airSpinInfluence *= THREE.MathUtils.clamp(this.speed, 0, 1);

        const flipSpeedFactor = THREE.MathUtils.clamp(1 - this.speed, 0, 1);
        const upFactor = (up.dot(new THREE.Vector3(0, -1, 0)) / 2) + 0.5;
        const flipOverInfluence = flipSpeedFactor * upFactor * 3;

        const maxAirSpinMagnitude = 2;
        const airSpinAcceleration = 0.15;
        const angVel = this.chassisBody.angularVelocity;

        const spinVectorForward = Utils.cannonVector(forward.clone());
        const spinVectorRight = Utils.cannonVector(right.clone());

        const effectiveSpinVectorForward = Utils.cannonVector(forward.clone().multiplyScalar(airSpinAcceleration * (airSpinInfluence + flipOverInfluence)));
        const effectiveSpinVectorRight = Utils.cannonVector(right.clone().multiplyScalar(airSpinAcceleration * (airSpinInfluence)));
        // Right
        if (steering > 0) {
            if (angVel.dot(spinVectorForward) < maxAirSpinMagnitude) {
                angVel.vadd(effectiveSpinVectorForward.scale(steering), angVel);
            }
        } else
            // Left
            if (steering < 0) {
                if (angVel.dot(spinVectorForward) > -maxAirSpinMagnitude) {
                    angVel.vadd(effectiveSpinVectorForward.scale(steering), angVel);
                }
            }

        // Forwards
        if (this.canTiltForwards && throttle > 0) {
            if (angVel.dot(spinVectorRight) < maxAirSpinMagnitude) {
                angVel.vadd(effectiveSpinVectorRight.scale(throttle), angVel);
            }
        } else
            // Backwards
            if (throttle < 0) {
                if (angVel.dot(spinVectorRight) > -maxAirSpinMagnitude) {
                    angVel.vadd(effectiveSpinVectorRight.scale(throttle), angVel);
                }
            }

        this.chassisBody.angularVelocity.copy(angVel);
    }
    /**
     * This method should be called in your main animation loop to update the car's state.
     * @param {number} timeStep - The time elapsed since the last frame.
     * @param {number} throttle - The throttle input, from -1 (reverse) to 1 (forward).
     * @param {number} steering - The steering input, from -1 (left) to 1 (right).
     */
    update(timeStep, throttle, steering) {
        if (!this.raycastVehicle) return;

        this.steeringValue = steering;

        // Calculate deceleration for use in wheel slip detection
        const currentForwardSpeed = this.getForwardSpeed();
        // Ensure timeStep is not zero to avoid division by zero
        const dt = timeStep > 0 ? timeStep : 1 / 60;
        this.deceleration = (this.lastForwardSpeed - currentForwardSpeed) / dt;
        this.lastForwardSpeed = currentForwardSpeed;


        const tiresHaveContact = this.raycastVehicle.numWheelsOnGround > 2;
        // Air spin
        if (!tiresHaveContact) {
            // Timer grows when car is off ground, resets once you touch the ground again
            this.airSpinTimer += timeStep;
            if (throttle <= 0) this.canTiltForwards = true;
        }
        else {
            this.canTiltForwards = false;
            this.airSpinTimer = 0;
        }

        // Update steering
        const steerTarget = -steering * this.vehicleParams.maxSteerVal;
        this.steeringSimulator.target = steerTarget;
        this.steeringSimulator.simulate(timeStep);
        this.setSteeringValue(this.steeringSimulator.position);

        this.airSpin(timeStep, throttle, steering);



        // Apply engine force
        const engineForce = this.vehicleParams.engineForce;
        const brakeForce = 7;

        // Get the car's forward speed (negative if reversing)
        const forwardSpeed = this.getForwardSpeed();

        // Reset forces
        this.applyEngineForce(0);
        this.setBrake(0);

        // Throttle / Forward
        if (throttle > 0) {
            this.applyEngineForce(-engineForce * throttle);
        }

        // Reverse / Brake
        if (throttle < 0) {
            if (forwardSpeed > 0.1) {
                // If moving forward, the reverse key acts as a brake
                this.setBrake(brakeForce * -throttle);
            } else {
                // If stopped or already moving backward, apply reverse force
                this.applyEngineForce(-engineForce / 2 * throttle); // Half power for reverse
            }
        }

        // Sync the 3D model with the physics body
        let pos = this.chassisBody.interpolatedPosition;
        this.chassisMesh.position.copy(this.chassisBody.interpolatedPosition);
        this.chassisMesh.quaternion.copy(this.chassisBody.interpolatedQuaternion);

        if (this.steeringWheel) {
            this.steeringWheel.rotation.z = -this.steeringSimulator.position * 2;
        }
        // Sync the wheel meshes with the physics simulation
        for (let i = 0; i < this.raycastVehicle.wheelInfos.length; i++) {
            this.raycastVehicle.updateWheelTransform(i);
            let transform = this.raycastVehicle.wheelInfos[i].worldTransform;

            let wheelObject = this.wheels[i].wheelObject;
            wheelObject.position.copy(new THREE.Vector3(transform.position.x, transform.position.y, transform.position.z));
            wheelObject.quaternion.copy(new THREE.Quaternion(transform.quaternion.x, transform.quaternion.y, transform.quaternion.z, transform.quaternion.w));

            let upAxisWorld = new CANNON.Vec3();
            this.raycastVehicle.getVehicleAxisWorld(this.raycastVehicle.indexUpAxis, upAxisWorld);
        }
    }

    getForwardSpeed() {
        if (!this.chassisBody) return 0;

        const forwardVector = new CANNON.Vec3(0, 0, 1);
        // Transform the local forward vector to world space using the chassis's quaternion
        this.chassisBody.quaternion.vmult(forwardVector, forwardVector);

        // Project the chassis's velocity onto the world-space forward vector
        const forwardSpeed = this.chassisBody.velocity.dot(forwardVector);
        return forwardSpeed;
    }

    // Add this helper method to your setBrake function for convenience
    setBrake(force) {
        for (let i = 0; i < this.raycastVehicle.wheelInfos.length; i++) {
            this.raycastVehicle.setBrake(force, i);
        }
    }

}
export default CarTest;